/* ---------------------------------------------------------------------- */
/*                           CreateQuest.jsx (dark/light)                 */
/* ---------------------------------------------------------------------- */
import React, { useState, useEffect } from "react";
import axios from "axios";
import TimePickerBottomSheet from "./TimePickerBottomSheet";

/* ====================== 다크 / 라이트 ====================== */
const COLOR_SET = {
  light: {
    bg: "#F8F6F2",
    card: "#FFFFFF",
    line: "#1D1D1D",
    grayLine: "#E3E4E9",
    place: "#C7C7CC",
    text: "#222222",
    beige: "#D8CBB2",
    toggleOn: "#FF9F1C",
    orangeTxt: "#FF9F1C",
    gray: "#888888",
    divider: "#E3E4E9"
  },
  dark: {
    bg: "#1A1A2E",
    card: "#1E3A52",
    line: "#FFFFFF",
    grayLine: "#36363D",
    place: "#AAAAAA",
    text: "#FFFFFF",
    beige: "#9DCAC9",
    toggleOn: "#22C4B6BF",
    orangeTxt: "#FFB627",
    gray: "#AAAAAA",
    divider: "#36363D"
  }
};

/* ==================== 환경변수 ==================== */
const API_URL = process.env.REACT_APP_API_URL; // ex) https://api.example.com

/* ==================== 공통 axios 인스턴스 ==================== */
const api = axios.create({ baseURL: API_URL });

/* --- 요청 시 accessToken 자동 첨부 --- */
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/* --- 401 → 토큰 재발급 후 재시도 --- */
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    if (err.response?.status !== 401) throw err;

    const original = err.config;
    if (original._retry) throw err; // 무한루프 방지

    original._retry = true;
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) throw err;

    try {
      const { data } = await axios.post(`${API_URL}/auth/token`, { refreshToken });
      const newAccess = data.token;
      localStorage.setItem("accessToken", newAccess);
      original.headers.Authorization = `Bearer ${newAccess}`;
      return api(original);
    } catch (e) {
      // 재발급 실패 → 로그아웃
      localStorage.clear();
      window.location.reload();
      throw e;
    }
  }
);

/* ==================== 달력 헬퍼 ==================== */
const getCalendar = (y, m) => {
  const first = new Date(y, m, 1).getDay();
  const total = new Date(y, m + 1, 0).getDate();
  const weeks = [];
  let w = new Array(first).fill(null);
  for (let d = 1; d <= total; d++) {
    w.push(d);
    if (w.length === 7) {
      weeks.push(w);
      w = [];
    }
  }
  if (w.length) weeks.push([...w, ...Array(7 - w.length).fill(null)]);
  while (weeks.length < 6) weeks.push(Array(7).fill(null));
  return weeks;
};
const toIsoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
const toKorean = (d) =>
  `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;

/* ==================== 컴포넌트 ==================== */
export default function CreateQuest({ onBack, initialData = null, onSaved }) {
  /* ---------- 테마 ---------- */
  const [theme, setTheme] = useState(
    localStorage.getItem("theme") === "dark" ? "dark" : "light"
  );
  const C = COLOR_SET[theme];

  useEffect(() => {
    const sync = () =>
      setTheme(localStorage.getItem("theme") === "dark" ? "dark" : "light");
    window.addEventListener("theme-changed", sync);
    return () => window.removeEventListener("theme-changed", sync);
  }, []);

  const isEdit = Boolean(initialData?._id);
  /* ---------- 기본 입력 ---------- */
  const [title, setTitle] = useState(initialData?.title ?? "");
  const [description, setDescription] = useState(initialData?.description ?? "");
  const [category, setCategory] = useState(
    initialData ? (initialData.type === "루틴" ? "루틴" : "투두") : "루틴"
  );

  /* ---------- 요일 ---------- */
  const weekKor = ["월", "화", "수", "목", "금", "토", "일"];
  const [selectedDays, setSelectedDays] = useState([]);
  const toggleDay = (d) =>
    setSelectedDays((p) => (p.includes(d) ? p.filter((x) => x !== d) : [...p, d]));
  const toggleAll = () => setSelectedDays((p) => (p.length === 7 ? [] : [...weekKor]));

  /* ---------- 기간 ---------- */
  const today = new Date();
  const initStart = initialData?.startDate
    ? new Date(initialData.startDate)
    : initialData?.date
    ? new Date(initialData.date)
    : today;
  const initEnd = initialData?.endDate ? new Date(initialData.endDate) : null;

  const [startDate, setStartDate] = useState(initStart);
  const [endDate, setEndDate] = useState(initEnd);
  const [showCal, setShowCal] = useState(false);
  const [editTarget, setEditTarget] = useState("start");
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());

  const openCal = (t) => {
    setEditTarget(t);
    const base = t === "start" ? startDate : endDate ?? startDate;
    setCalYear(base.getFullYear());
    setCalMonth(base.getMonth());
    setShowCal(true);
  };
  const pickDate = (d) => {
    if (!d) return;
    const sel = new Date(calYear, calMonth, d);
    if (editTarget === "start") {
      setStartDate(sel);
      if (endDate && sel > endDate) setEndDate(sel);
    } else {
      setEndDate(sel);
      if (sel < startDate) setStartDate(sel);
    }
    setShowCal(false);
  };
  const prevMonth = () => {
    setCalMonth((m) => (m === 0 ? 11 : m - 1));
    if (calMonth === 0) setCalYear((y) => y - 1);
  };
  const nextMonth = () => {
    setCalMonth((m) => (m === 11 ? 0 : m + 1));
    if (calMonth === 11) setCalYear((y) => y + 1);
  };

  /* ---------- 알람 ---------- */
  const [timerEnabled, setTimerEnabled] = useState(!!initialData?.alarmTime);
  const defaultTime = initialData?.alarmTime
    ? {
        hour: Number(initialData.alarmTime.split(":"))[0],
        minute: Number(initialData.alarmTime.split(":"))[1],
        ampm: "AM",
      }
    : { hour: 0, minute: 0, ampm: "PM" };
  const [timer, setTimer] = useState(defaultTime);
  const [alertMode, setAlertMode] = useState(
    initialData?.alarmOption?.repeat
      ? "반복울림"
      : initialData?.alarmOption?.sound
      ? "소리"
      : "진동"
  );

  const [showTP, setShowTP] = useState(false);
  const toTime = () =>
    `${String(timer.hour).padStart(2, "0")}:${String(timer.minute).padStart(2, "0")}`;

  /* ---------- 저장 ---------- */
  const maxTitle = 12,
    maxDesc = 100;
  const handleSave = async () => {
    if (!title.trim()) return alert("제목을 입력해 주세요");

    // ----- 타입별 필수값 검증 -----
    if (category === "루틴") {
      if (!startDate) return alert("시작일을 설정해 주세요");
      if (selectedDays.length === 0) return alert("반복할 요일을 최소 1개 선택해 주세요");
    } else {
      if (!startDate) return alert("날짜를 선택해 주세요");
    }

    const payload = {
      type: category === "루틴" ? "routine" : "todo",
      title,
      description,
    };

    /* 알람 */
    if (timerEnabled) {
      payload.alarmTime = toTime();
      payload.alarmOption = {
        sound: alertMode === "소리",
        vibrate: alertMode === "진동",
        repeat: alertMode === "반복울림",
      };
    }

    /* 기간 / 요일 */
    if (payload.type === "routine") {
      payload.daysOfWeek = selectedDays.map((k) => (weekKor.indexOf(k) + 1) % 7);
      payload.startDate = toIsoDate(startDate);
      payload.endDate = toIsoDate(endDate ?? new Date(9999, 11, 25));
    } else {
      payload.date = toIsoDate(startDate);
    }

    try {
      const { data } = isEdit
        ? await api.patch(`/quest/${initialData._id}`, payload)
        : await api.post("/quest", payload);
      alert(isEdit ? "퀘스트가 수정되었습니다!" : "퀘스트가 등록되었습니다!");
      onSaved?.();
    } catch (err) {
      console.error("❌ /quest error:", err.response ?? err);
      alert(err.response?.data?.message || err.message || "퀘스트 생성에 실패했습니다");
    }
  };

  /* ==================== UI ==================== */
  return (
    <div
      style={{
        width: 393,
        minHeight: "100vh",
        margin: "0 auto",
        background: C.bg,
        fontFamily: "'Pretendard', sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ---------- 헤더 ---------- */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 30,
          height: 56,
          background: C.bg,
          display: "flex",
          alignItems: "center",
          borderBottom: `1px solid ${C.grayLine}`,
          padding: "0 8px",
        }}
      >
        <button
          onClick={onBack}
          style={{
            width: 40,
            height: 40,
            background: "none",
            border: "none",
            fontSize: 24,
            color: C.gray,
            cursor: "pointer",
          }}
        >
          ‹
        </button>
        <h1
          style={{
            flex: 1,
            textAlign: "center",
            fontSize: 22,
            fontWeight: 800,
            color: C.text,
          }}
        >
          {isEdit ? "퀘스트 수정하기" : "퀘스트 생성하기"}
        </h1>
        <button
          onClick={handleSave}
          style={{
            minWidth: 40,
            background: "none",
            border: "none",
            color: C.orangeTxt,
            fontWeight: 700,
            fontSize: 16,
            cursor: "pointer",
          }}
        >
          완료
        </button>
      </header>

      {/* ---------- 스크롤 영역 ---------- */}
      <div style={{ flex: 1, overflowY: "auto", padding: "24px 20px" }}>
        {/* ----- 제목 ----- */}
        <div style={{ position: "relative", marginBottom: 24 }}>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, maxTitle))}
            placeholder="퀘스트 이름을 입력해 주세요"
            maxLength={maxTitle}
            style={{
              boxSizing: "border-box", 
              width: "100%",
              height: 48,
              padding: "0 18px",
              borderRadius: 12,
              border: `1.5px solid ${C.line}`,
              fontSize: 16,
              fontWeight: 500,
              color: C.text,
              background: C.card,
              outline: "none",
            }}
          />
          <span
            style={{
              position: "absolute",
              right: 46,
              top: 13,
              fontSize: 12,
              color: C.gray,
            }}
          >
            {String(title.length).padStart(2, "0")}/{maxTitle}
          </span>
          {title && (
            <button
              onClick={() => setTitle("")}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: "none",
                background: C.gray,
                color: C.card,
                fontSize: 16,
                lineHeight: "24px",
                cursor: "pointer",
              }}
            >
              ×
            </button>
          )}
        </div>

        {/* ----- 설명 ----- */}
        <div style={{ position: "relative", marginBottom: 32 }}>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, maxDesc))}
            rows={4}
            maxLength={maxDesc}
            style={{
              boxSizing: "border-box",
              width: "100%",
              minHeight: 120,
              padding: "16px 18px",
              borderRadius: 12,
              border: `1.5px solid ${C.line}`,
              fontSize: 16,
              fontWeight: 500,
              color: C.text,
              background: C.card,
              outline: "none",
              resize: "none",
            }}
          />
          {!description && (
            <div
              style={{
                position: "absolute",
                inset: 16,
                pointerEvents: "none",
                fontSize: 0,
                lineHeight: "16px",
              }}
            >
              {"○".repeat(100)
                .match(/.{1,25}/g)
                .map((row, i) => (
                  <span
                    key={i}
                    style={{
                      display: "block",
                      color: C.place,
                      letterSpacing: "3px",
                    }}
                  >
                    {row}
                  </span>
                ))}
            </div>
          )}
          <span
            style={{
              position: "absolute",
              right: 46,
              top: 13,
              fontSize: 12,
              color: C.gray,
            }}
          >
            {String(description.length).padStart(2, "0")}/{maxDesc}
          </span>
          {description && (
            <button
              onClick={() => setDescription("")}
              style={{
                position: "absolute",
                right: 10,
                top: 10,
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: "none",
                background: C.gray,
                color: C.card,
                fontSize: 16,
                lineHeight: "24px",
                cursor: "pointer",
              }}
            >
              ×
            </button>
          )}
        </div>

        {/* ----- 카테고리 탭 ----- */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 32,
            marginBottom: 32,
          }}
        >
          {["루틴", "투두"].map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              style={{
                minWidth: 80,
                height: 48,
                background: category === c ? C.beige : "transparent",
                color: category === c ? C.text : C.gray,
                border: "none",
                borderRadius: 14,
                fontWeight: 800,
                fontSize: 17,
                cursor: "pointer",
              }}
            >
              {c}
            </button>
          ))}
        </div>

        {/* ----- 요일 선택 ----- */}
        {category === "루틴" && (
          <section
            style={{
              border: `1.5px solid ${C.line}`,
              borderRadius: 12,
              padding: "16px 18px",
              background: C.card,
              marginBottom: 24,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 14,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", fontSize: 16 }}>
                <span style={{ marginRight: 6, fontSize: 18, color: C.gray }}>↻</span>
                날짜
              </div>
              <button
                onClick={toggleAll}
                style={{
                  display: "flex",
                  alignItems: "center",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: C.gray,
                  fontSize: 14,
                }}
              >
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    border: `1.5px solid ${C.gray}`,
                    marginRight: 4,
                    background: selectedDays.length === 7 ? C.gray : "none",
                  }}
                />
                모두 선택
              </button>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "nowrap" }}>
              {weekKor.map((d) => {
                const sel = selectedDays.includes(d);
                return (
                  <button
                    key={d}
                    onClick={() => toggleDay(d)}
                    style={{
                      flex: "1 1 0",
                      minWidth: 0,
                      height: 44,
                      flexShrink: 0,
                      borderRadius: 12,
                      background: sel ? C.beige : "transparent",
                      border: `1.5px solid ${sel ? C.beige : C.line}`,
                      color: C.text,
                      fontSize: 17,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* ----- 기간 ----- */}
        <section
          style={{
            border: `1.5px solid ${C.line}`,
            borderRadius: 12,
            background: C.card,
            padding: "16px 18px",
            boxSizing: "border-box",
            marginBottom: 24,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
            <span style={{ fontSize: 18, color: C.gray, marginRight: 6 }}>📅</span>
            기간
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            {category === "루틴" ? (
              <>
                <button
                  onClick={() => openCal("start")}
                  style={{
                    flex: 1,
                    minWidth: 120,
                    height: 44,
                    border: "none",
                    borderRadius: 14,
                    background: C.beige,
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {toKorean(startDate)}
                </button>

                <span style={{ fontSize: 24 }}>~</span>

                <button
                  onClick={() => openCal("end")}
                  style={{
                    flex: 1,
                    minWidth: 120,
                    height: 44,
                    border: "none",
                    borderRadius: 14,
                    background: C.beige,
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {endDate ? toKorean(endDate) : "반복"}
                </button>
              </>
            ) : (
              <button
                onClick={() => openCal("start")}
                style={{
                  flex: 1,
                  minWidth: 120,
                  height: 44,
                  border: "none",
                  borderRadius: 14,
                  background: C.beige,
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {toKorean(startDate)}
              </button>
            )}
          </div>
        </section>

        {/* ----- 시간 & 알림 ----- */}
        <section
          style={{
            border: `1.5px solid ${C.line}`,
            borderRadius: 12,
            background: C.card,
            padding: "16px 18px",
            boxSizing: "border-box",
            marginBottom: 24,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 14,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", fontSize: 16 }}>
              <span style={{ fontSize: 18, color: C.gray, marginRight: 6 }}>🔔</span>
              시간
            </div>

            <label style={{ position: "relative", width: 52, height: 30 }}>
              <input
                type="checkbox"
                checked={timerEnabled}
                onChange={() => setTimerEnabled((p) => !p)}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span
                style={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: 15,
                  background: timerEnabled ? C.toggleOn : C.grayLine,
                  transition: "0.25s",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  top: 3,
                  left: timerEnabled ? 26 : 3,
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: C.card,
                  transition: "0.25s",
                }}
              />
            </label>
          </div>

          {timerEnabled && (
            <>
              <div
                onClick={() => setShowTP(true)}
                style={{
                  height: 48,
                  borderRadius: 12,
                  background: C.beige,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 20,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {toTime()} {timer.ampm}
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 18,
                }}
              >
                {["소리", "진동", "반복울림"].map((m) => (
                  <button
                    key={m}
                    onClick={() => setAlertMode(m)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 16,
                      color: alertMode === m ? C.text : C.gray,
                      fontWeight: alertMode === m ? 700 : 500,
                    }}
                  >
                    <span
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        border:
                          alertMode === m
                            ? `8px solid ${C.text}`
                            : `1.5px solid ${C.grayLine}`,
                        boxSizing: "border-box",
                      }}
                    />
                    {m}
                  </button>
                ))}
              </div>
            </>
          )}
        </section>
      </div>

      {/* ---------- 시간 선택 BottomSheet ---------- */}
      <TimePickerBottomSheet
        open={showTP}
        onClose={() => setShowTP(false)}
        onSelect={(t) => setTimer(t)}
        initialValue={timer}
      />

      {/* ---------- 달력 BottomSheet ---------- */}
      {showCal && (
        <>
          <div
            onClick={() => setShowCal(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.25)",
              zIndex: 40,
            }}
          />
          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100vw",
              maxWidth: 393,
              background: C.card,
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              padding: 24,
              zIndex: 41,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <button
                onClick={prevMonth}
                style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer" }}
              >
                ‹
              </button>
              <span style={{ fontSize: 18, fontWeight: 600 }}>
                {calYear}년 {calMonth + 1}월
              </span>
              <button
                onClick={nextMonth}
                style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer" }}
              >
                ›
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7,1fr)",
                marginBottom: 8,
                textAlign: "center",
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              {["일", "월", "화", "수", "목", "금", "토"].map((d, i) => (
                <div key={d} style={{ color: i === 0 ? "#F25C5C" : i === 6 ? "#3B82F6" : C.text }}>
                  {d}
                </div>
              ))}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7,1fr)",
                gap: 6,
                marginBottom: 24,
              }}
            >
              {getCalendar(calYear, calMonth).flatMap((week, r) =>
                week.map((d, c) => {
                  const cur = editTarget === "start" ? startDate : endDate ?? new Date(0);
                  const isSel =
                    cur.getFullYear() === calYear &&
                    cur.getMonth() === calMonth &&
                    cur.getDate() === d;

                  return (
                    <button
                      key={`${r}-${c}`}
                      disabled={!d}
                      onClick={() => pickDate(d)}
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 8,
                        border: isSel ? `1.5px solid ${C.line}` : "none",
                        background: isSel ? "transparent" : C.card,
                        color: d ? C.text : C.place,
                        position: "relative",
                        cursor: d ? "pointer" : "default",
                      }}
                    >
                      {isSel && (
                        <span
                          style={{
                            position: "absolute",
                            inset: 4,
                            borderRadius: "50%",
                            background: C.beige,
                            zIndex: -1,
                          }}
                        />
                      )}
                      {d || ""}
                    </button>
                  );
                })
              )}
            </div>

            <button
              onClick={() => {
                setEndDate(null);
                setShowCal(false);
              }}
              style={{
                width: "100%",
                height: 48,
                border: "none",
                borderRadius: 14,
                background: C.beige,
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              반복
            </button>
          </div>
        </>
      )}
    </div>
  );
}
